import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';

import {
  SCHEDULE_STATUS_RATING_KEY,
  UNKNOWN_FREQUENCY_LABEL,
  useSchedulerOneOffLabel,
} from './config';

/**
 * The ribbon drives the table's client-side property filter, so every tile's
 * value must be the translated label the row carries, not the underlying status
 * key — otherwise a label edit leaves a tile matching nothing. Taking the labels
 * from the same taxonomy the column renders keeps the two in step, including
 * under a tenant override.
 */
export const useGetDefaultRibbonFilters = (): FilterModal[] => {
  const { t } = useTranslation(['common'], { keyPrefix: 'scheduler.tiles' });
  const { getLabel } = useRating(SCHEDULE_STATUS_RATING_KEY);
  const oneOffLabel = useSchedulerOneOffLabel();

  return useMemo(
    () => [
      {
        id: uuidv4(),
        title: t('all'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('due'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'StatusLabelled',
              value: getLabel('due'),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('overdue'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'StatusLabelled',
              value: getLabel('overdue'),
            },
          ],
          operation: 'and',
        },
      },
      {
        id: uuidv4(),
        title: t('complete'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'StatusLabelled',
              value: getLabel('complete'),
            },
            {
              operator: '=',
              propertyKey: 'StatusLabelled',
              value: getLabel('complete_overdue'),
            },
          ],
          operation: 'or',
        },
      },
      {
        id: uuidv4(),
        title: t('recurring'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            { operator: '!=', propertyKey: 'Frequency', value: oneOffLabel },
            {
              operator: '!=',
              propertyKey: 'Frequency',
              value: UNKNOWN_FREQUENCY_LABEL,
            },
          ],
          operation: 'and',
        },
      },
    ],
    [t, getLabel, oneOffLabel]
  );
};
